var _ = require("lodash");

/***{@linkmodule:TypesType}isanobjectrepresentingatypeof
 *   the type system used in `comp` package.
 *
 * Since an extension of the gradual type system is used,
 *   there are three possible basic classes of `Type`:
 *   - {@link module:Types~BasicType BasicType} or `String`,
 *   - {@link module:Types~FunctionType FunctionType} - functional type,
 *   - {@link module:Types~AnyType AnyType} - a dynamic type.
 *
 * `Type` is open, any object can be a type.
 * Implement {@link module:Types.eq eq} and/or
 *   {@link module:Types.compat compat} methods of an object or its prototype
 *   to specify how these types must be compared.
 *
 * @typedef Type
 * @see module:Types
 */

/**
 * module configuration
 */
var config = {
  /**
   * @name config.showWarnings
   * @memberof module:Types
   */
  showWarnings: false
};

/**
 * Constructor of a basic type.
 * It is just a wrapper over String.
 *
 * Two basic types are equal iff their names are equal.
 * 
 * Other types and functions should be implemented with an assumption
 * that `str` and `BasicType(str)` are equivalent.
 *
 * @param {String} name
 */
function BasicType(name) {
  if(!_.isEmpty(this)) return new BasicType(name);
  this.name = name;
}

BasicType.prototype = {
  eq: function(t) {
    if(t instanceof BasicType)
      return this.name === t.name;
    if(_.isString(t))
      return this.name === t;
    return false;
  },
  compat: function(t) {
    return t === AnyType || this.eq(t);
  }
};

/**
 * Constructor of a functional type `A -> B`.
 * @param dom - domain type A
 * @param cod - codomain type B
 */
function FunctionType(dom, cod) {
  if(!_.isEmpty(this)) return new FunctionType(dom, cod);
  this.dom = dom;
  this.cod = cod;
}

FunctionType.prototype = {
  eq: function(t) {
    if(t instanceof FunctionType)
      return compareTypes(this.dom, t.dom) && compareTypes(this.cod, t.cod);
    return false;
  },
  compat: function(t) {
    if(t instanceof FunctionType)
      return compatible(this.dom, t.dom) && compatible(this.cod, t.cod);
    if(t === AnyType) return true;
    return false;
  }
};

/**
 * AnyType is a singleton type represinting a dynamic type
 * AnyType is compatible to any other type.
 */
var AnyType = (function() {
  // it is just a way to declare a singleton with its constructor
  // of the same name
  function AnyType() {}
  AnyType.prototype.eq = function(t) { return this === t; };
  AnyType.prototype.compat = function(t) { return true; };
  var inst = new AnyType;
  return inst;
})();

/**
 * @summary Basic type inference rule
 * @description
 * Takes two types (functional `A -> B` and argument `A`)
 *   and returns `B` (a type of an application).
 * If the first type is {@link module:Types.AnyType AnyType},
 *   returns {@link module:Types.AnyType AnyType} as well.
 * @param {Type} ft - a lambda type of function term `x`
 * @param {Type} at - a lambda type of argument term `y`
 * @return a type of `(xy)`, which is
 *   `ft.cod` if `ft` is `{@link module:Types.FunctionType}(dom, cod)`,
 *    or {@link module:Types.AnyType AnyType} if `ft == AnyType`.
 * @throws an exception
 *   - when `ft` is neither a functional type nor AnyType
 * @throws a warning
 *   - when `at` and `ft.dom` differs
 *   - when `at` is AnyType
 */
function ModusPonens(ft, at) {
  var funcType = simplifyType(ft);
  if(funcType === AnyType) return AnyType;
  if(! (funcType instanceof FunctionType)) {
    if(config.showWarnings) {
      console.error("an applying term has non-functional type");
      console.warn(funcType);
    }
    return AnyType;
  }
  var argType = simplifyType(at);
  if(argType === AnyType && config.showWarnings) {
    console.warn("Argument has no specified type");
    console.warn(at);
    console.warn("Functional type is: ", ft);
  }
  if(!compatible(funcType.dom, argType)) {
    if(config.showWarnings) {
      console.error("Warning: types of formal and actual arguments are different");
      console.warn(ft);
      console.warn(at);
    }
    throw "incompatible types!";
  }
  return funcType.cod;
};

/**
 * convert a type to its normal form.
 * For a simply-typed calculus it does nothing but
 *   converting String types to BasicType
 * @param {Type} type - a type to simplify
 * @return an equivalent type.
 *   Basically, this method might modify given type and return the same
 *   JS object, that is, providing side effects.
 * @deprecated
 */
function simplifyType(type) {
  if(type === undefined) return AnyType;
  if(_.isString(type)) return new BasicType(type);
  if(type instanceof FunctionType)
    return new FunctionType(simplifyType(type.dom), simplifyType(type.cod));
  return type;
}

/**
 * This method is implemented by an {@link Type} object (or its prototype)
 *   to specify how to compare two type on syntactic level.
 * This method is important when there is a constructor (like `FunctionType`)
 *   that returns a referentially fresh object,
 *   so that each type can be represented with many different objects.
 * @summary a method of {@link Type} instance.
 * @callback eq
 * @param {Type} b - a type to compare with
 * @return a boolean value.
 * @see module:Types~compareTypes
 */

/**
 * compare structurally two types.
 * If none of two types provides {@link module:Types~eq eq} method,
 *   both arguments are compared with `==`.
 * This function works on syntactic level and
 *   might not be symmetric nor transitive.
 * Its purpose is to check *fast* whether two objects represent
 *   syntactically equal types.
 * @param {Type} a
 * @param {Type} b
 * @return true if a and b are structurally equal, false otherwise
 */
function compareTypes(a, b) {
  if(!a || !a.eq) {
    if(!b || !b.eq)
      return a == b;
    else
      return b.eq(a);
  }
  return a.eq(b);
}

/**
 * compare two types after simplification.
 * @param {Type} a
 * @param {Type} b
 * @return true if a and b are equal up to simplifying conversion
 * @deprecated
 */
function equivTypes(a, b) {
  return compareTypes(simplifyType(a), simplifyType(b));
}

/**
 * This method is implemented by an {@link Type} object (or its prototype)
 *   to specify compatibility of two type.
 * @summary a method of {@link Type} instance.
 * @callback compat
 * @param {Type} b - a type to compare with
 * @return a boolean value.
 * @see module:Types~compatible
 */

/**
 * stand-alone function based on `compat` method.
 * By default, the following statements hold
 *   - `AnyType` is compatible to everything,
 *   - a basic type is compatible only to itself,
 *   - a functional type is compatible to another functional type when both
 *   domains and codomains are compatible.
 *
 * This predicate is reflexive and symmetric, but not transitive.
 *
 * If none of two types implements {@link module:Types~compat compat} method,
 *   {@link module:Types~compareTypes} is used.
 * @param {Type} a
 * @param {Type} b
 * @return `true` iff both types are compatible.
 */
function compatible(a, b) {
  if(!a || !a.compat) {
    if(_.isString(a))
      return new BasicType(a).compat(b);
    return compareTypes(a, b) || compareTypes(b, a);
  }
  return a.compat(b);
}

module.exports = {
    config: config,
    BasicType: BasicType,
    FunctionType: FunctionType,
    AnyType: AnyType,
    ModusPonens: ModusPonens,
    compare: compareTypes,
    equiv: equivTypes,
    compatible: compatible,
    simplify: simplifyType
  };
